import React from 'react';
import { ProblemSection } from '../../services/surveyService';

interface Props {
  sections: ProblemSection[];
  onSectionClick: (gisId: string) => void;
}

const ProblemSectionsCard: React.FC<Props> = ({ sections, onSectionClick }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const highPriority = sections.filter(s => s.priority === 'High');
  const mediumPriority = sections.filter(s => s.priority === 'Medium');
  const lowPriority = sections.filter(s => s.priority === 'Low');

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">
            Problem Sections ({sections.length})
          </h2>
        </div>
      </div>

      <div className="p-6">
        {sections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 text-green-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium text-green-600">Great! No problem sections found.</p>
            <p className="text-sm text-gray-500 mt-1">All sections are performing well based on user feedback.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* High Priority */}
            {highPriority.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-red-700 mb-2">High Priority ({highPriority.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {highPriority.map((section) => (
                    <div key={section.gisId} className="border border-red-200 rounded-lg p-3 bg-red-50">
                      <div className="flex justify-between items-start">
                        <button
                          onClick={() => onSectionClick(section.gisId)}
                          className="text-sm font-medium text-red-900 hover:text-red-700"
                        >
                          {section.sectionName}
                        </button>
                        <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(section.priority)}`}>
                          {section.priority}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-red-700">
                        {section.issues.slice(0, 2).map((issue, index) => (
                          <div key={index}>• {issue}</div>
                        ))}
                        {section.issues.length > 2 && (
                          <div>• +{section.issues.length - 2} more issues</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medium Priority */}
            {mediumPriority.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-yellow-700 mb-2">Medium Priority ({mediumPriority.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {mediumPriority.map((section) => (
                    <div key={section.gisId} className="border border-yellow-200 rounded-lg p-3 bg-yellow-50">
                      <div className="flex justify-between items-start">
                        <button
                          onClick={() => onSectionClick(section.gisId)}
                          className="text-sm font-medium text-yellow-900 hover:text-yellow-700"
                        >
                          {section.sectionName}
                        </button>
                        <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(section.priority)}`}>
                          {section.priority}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-yellow-700">
                        {section.issues.slice(0, 2).map((issue, index) => (
                          <div key={index}>• {issue}</div>
                        ))}
                        {section.issues.length > 2 && (
                          <div>• +{section.issues.length - 2} more issues</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Low Priority */}
            {lowPriority.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-blue-700 mb-2">Low Priority ({lowPriority.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {lowPriority.map((section) => (
                    <div key={section.gisId} className="border border-blue-200 rounded-lg p-3 bg-blue-50">
                      <div className="flex justify-between items-start">
                        <button
                          onClick={() => onSectionClick(section.gisId)}
                          className="text-sm font-medium text-blue-900 hover:text-blue-700"
                        >
                          {section.sectionName}
                        </button>
                        <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(section.priority)}`}>
                          {section.priority}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-blue-700">
                        {section.issues.slice(0, 2).map((issue, index) => (
                          <div key={index}>• {issue}</div>
                        ))}
                        {section.issues.length > 2 && (
                          <div>• +{section.issues.length - 2} more issues</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemSectionsCard;